import React from "react";
import { useAnalyticsDashboard } from "../hooks/useAnalyticsDashboard";

type DashboardContextValue = {
  available: boolean | null;
  loading: boolean;
  embedUrl: string | null;
  name: string | null;
  initialFrom: string | null;
  initialTo: string | null;
  refresh: () => Promise<void>;
};

const DashboardContext = React.createContext<DashboardContextValue>({
  available: null,
  loading: true,
  embedUrl: null,
  name: null,
  initialFrom: null,
  initialTo: null,
  refresh: async () => {},
});

export const useDashboard = () => React.useContext(DashboardContext);

export const DashboardProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { fetchDashboardConfig } = useAnalyticsDashboard();
  const [available, setAvailable] = React.useState<boolean | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [embedUrl, setEmbedUrl] = React.useState<string | null>(null);
  const [name, setName] = React.useState<string | null>(null);
  const [initialFrom, setInitialFrom] = React.useState<string | null>(null);
  const [initialTo, setInitialTo] = React.useState<string | null>(null);

  const fetchDashboardConfigRef = React.useRef(fetchDashboardConfig);
  fetchDashboardConfigRef.current = fetchDashboardConfig;

  const refresh = React.useCallback(async () => {
    try {
      const config = await fetchDashboardConfigRef.current();
      if (config.available) {
        setAvailable(true);
        setEmbedUrl(config.embedUrl);
        setName(config.name || null);
        setInitialFrom(config.from || null);
        setInitialTo(config.to || null);
      } else {
        setAvailable(false);
        setEmbedUrl(null);
        setName(null);
        setInitialFrom(null);
        setInitialTo(null);
      }
    } catch {
      setAvailable(false);
      setEmbedUrl(null);
      setName(null);
      setInitialFrom(null);
      setInitialTo(null);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const config = await fetchDashboardConfigRef.current();
        if (cancelled) return;

        if (config.available) {
          setAvailable(true);
          setEmbedUrl(config.embedUrl);
          setName(config.name || null);
          setInitialFrom(config.from || null);
          setInitialTo(config.to || null);
        } else {
          setAvailable(false);
          setEmbedUrl(null);
          setName(null);
          setInitialFrom(null);
          setInitialTo(null);
        }
      } catch {
        if (cancelled) return;
        setAvailable(false);
        setEmbedUrl(null);
        setName(null);
        setInitialFrom(null);
        setInitialTo(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <DashboardContext.Provider
      value={{
        available,
        loading,
        embedUrl,
        name,
        initialFrom,
        initialTo,
        refresh,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};
